import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@/contexts/PermissionsContext';
import type { SettingsPermissions, ReportPermissions } from '@/types/roles.types';
import { ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface SettingGuardProps {
  setting: keyof SettingsPermissions;
  children: React.ReactNode;
}

interface ReportGuardProps {
  report: keyof ReportPermissions;
  children: React.ReactNode;
}

function PermissionDenied({ backPath, backLabel }: { backPath: string; backLabel: string }) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-1 items-center justify-center p-8">
      <div className="flex flex-col items-center gap-4 text-center max-w-md">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
          <ShieldAlert className="h-8 w-8 text-destructive" />
        </div>
        <h2 className="text-xl font-semibold">Access Denied</h2>
        <p className="text-sm text-muted-foreground">
          You do not have permission to access this page. Contact your administrator to request access.
        </p>
        <Button variant="outline" onClick={() => navigate(backPath)}>
          {backLabel}
        </Button>
      </div>
    </div>
  );
}

export function SettingGuard({ setting, children }: SettingGuardProps) {
  const { canAccessSetting, isLoading } = usePermissions();
  if (isLoading) return null;
  if (!canAccessSetting(setting))
    return <PermissionDenied backPath="/settings" backLabel="Back to Settings" />;
  return <>{children}</>;
}

export function ReportGuard({ report, children }: ReportGuardProps) {
  const { reports, isLoading } = usePermissions();
  if (isLoading) return null;
  if (!reports[report])
    return <PermissionDenied backPath="/dashboard" backLabel="Back to Dashboard" />;
  return <>{children}</>;
}

export function AuditLogGuard({ children }: { children: React.ReactNode }) {
  const { reports, isLoading } = usePermissions();
  if (isLoading) return null;
  if (!reports.auditLogReport)
    return <PermissionDenied backPath="/dashboard" backLabel="Back to Dashboard" />;
  return <>{children}</>;
}
